import { isApiError, type SessionState } from '@avrahami/ui';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { sessionQuery } from './api/session';
import { setActiveTenant, STORE_CHANGED_MESSAGE } from './lib/api';
import { listenToOtherTabs, reloadSession } from './lib/session-sync';
import { createAppRouter } from './router';
import './styles.css';

/**
 * Any call that finds the session gone (expired, revoked, waiting for its second factor) or the
 * store suspended makes the app re-read the session: the layout then sends the member to the right
 * screen, or shows the suspended-store banner. A call refused because another tab moved the
 * session to another store drops everything cached and starts again from the session.
 */
function onApiError(error: unknown): void {
  if (isApiError(error, 'CONFLICT') && error.message === STORE_CHANGED_MESSAGE) {
    reloadSession(queryClient);
    return;
  }
  if (
    isApiError(error, 'UNAUTHENTICATED') ||
    isApiError(error, 'SESSION_EXPIRED') ||
    isApiError(error, 'MFA_REQUIRED') ||
    isApiError(error, 'TENANT_SUSPENDED')
  ) {
    void queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
  }
}

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.queryKey[0] !== sessionQuery.queryKey[0]) onApiError(error);
    },
  }),
  mutationCache: new MutationCache({ onError: onApiError }),
  defaultOptions: {
    queries: {
      retry: (failures, error) => !isApiError(error) && failures < 2,
      refetchOnWindowFocus: true,
      // A list someone returns to is still in memory, so it shows at once, at the same scroll
      // position, and refreshes behind the scenes.
      gcTime: 30 * 60 * 1000,
    },
  },
});

// The store this tab works on follows its session: query keys and the x-expected-tenant header.
const queryCache = queryClient.getQueryCache();
queryCache.subscribe((event) => {
  const session = queryCache.find({ queryKey: sessionQuery.queryKey, exact: true });
  if (session === undefined || event.query !== session) return;
  const state = queryClient.getQueryData<SessionState>(sessionQuery.queryKey);
  setActiveTenant(state?.kind === 'signed-in' ? state.me.activeTenant?.tenantId : undefined);
});
listenToOtherTabs(queryClient);

const router = createAppRouter(queryClient);

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');
createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
